import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useCookieConsent } from '../../contexts/CookieConsentContext/CookieConsentContext';
import './style.less';

export const CookieConsent = () => {
  const { t } = useTranslation();
  const { hasConsent, isLoaded, acceptAll, acceptNecessary, updateConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  // Don't render if not loaded or user has already consented
  if (!isLoaded || hasConsent) {
    return null;
  }

  const handleSaveSettings = () => {
    updateConsent({ analytics: analyticsEnabled, marketing: marketingEnabled });
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {!showSettings && (
          <motion.div
            className="cookie-banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="cookie-banner-content">
              <div className="cookie-banner-text">
                <h4>{t('cookies.banner.title')}</h4>
                <p>
                  {t('cookies.banner.description')}{' '}
                  {t('cookies.banner.learnMore')}{' '}
                  <Link to="/privacy-policy">{t('cookies.banner.privacyPolicy')}</Link>.
                </p>
              </div>
              <div className="cookie-banner-actions">
                <button
                  className="btn-cookie btn-cookie-secondary"
                  onClick={acceptNecessary}
                  type="button"
                  aria-label={t('cookies.banner.acceptNecessary')}
                >
                  {t('cookies.banner.acceptNecessary')}
                </button>
                <button
                  className="btn-cookie btn-cookie-secondary"
                  onClick={handleOpenSettings}
                  type="button"
                  aria-label={t('cookies.banner.settings')}
                >
                  {t('cookies.banner.settings')}
                </button>
                <button
                  className="btn-cookie btn-cookie-primary"
                  onClick={acceptAll}
                  type="button"
                  aria-label={t('cookies.banner.acceptAll')}
                >
                  {t('cookies.banner.acceptAll')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <>
            <motion.div
              className="cookie-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseSettings}
              aria-hidden="true"
            />
            <motion.div
              className="cookie-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-modal-title"
            >
              <motion.div
                className="cookie-modal-content"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                <div className="cookie-modal-header">
                <h3 id="cookie-modal-title">{t('cookies.modal.title')}</h3>
                <button
                  className="cookie-modal-close"
                  onClick={handleCloseSettings}
                  type="button"
                  aria-label="Close"
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              </div>

              <div className="cookie-modal-body">
                <p className="cookie-modal-description">{t('cookies.modal.description')}</p>

                {/* Necessary Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>{t('cookies.modal.necessary.title')}</h4>
                      <p>{t('cookies.modal.necessary.description')}</p>
                    </div>
                    <span className="cookie-option-badge">{t('cookies.modal.necessary.always')}</span>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>{t('cookies.modal.analytics.title')}</h4>
                      <p>{t('cookies.modal.analytics.description')}</p>
                    </div>
                    <label className="cookie-toggle" htmlFor="analytics-toggle">
                      <input
                        type="checkbox"
                        id="analytics-toggle"
                        checked={analyticsEnabled}
                        onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                      />
                      <span className="cookie-toggle-slider"></span>
                    </label>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>{t('cookies.modal.marketing.title')}</h4>
                      <p>{t('cookies.modal.marketing.description')}</p>
                    </div>
                    <label className="cookie-toggle" htmlFor="marketing-toggle">
                      <input
                        type="checkbox"
                        id="marketing-toggle"
                        checked={marketingEnabled}
                        onChange={(e) => setMarketingEnabled(e.target.checked)}
                      />
                      <span className="cookie-toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

                <div className="cookie-modal-footer">
                  <button
                    className="btn-cookie btn-cookie-primary"
                    onClick={handleSaveSettings}
                    type="button"
                  >
                    {t('cookies.modal.save')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
